import { webApiService } from '@/services'
import { Modal, ModalBody, ModalContent, ModalHeader, Radio, RadioGroup, Skeleton } from '@nextui-org/react'
import { motion } from 'framer-motion'
import { enqueueSnackbar } from 'notistack'
import React, { ChangeEvent, CSSProperties, useEffect, useState } from 'react'
import { MdOutlinePhotoCamera } from 'react-icons/md'
import { useFormularioClienteContext } from '../hooks/useFormularioClienteContext'
import { IConfigSeller } from '@/models/configSeller'
import { sinTrizaduras } from '@/utils/questionText'
import { getQuestionExampleImages, hasQuestionExampleImage } from '@/utils/questionImages'
import { useSelector } from 'react-redux'

const StepTwo: React.FC = () => {
  const { ctx, saveCtx } = useFormularioClienteContext()
  const [data, setData] = useState<{ loading: boolean, questionList: any[] }>({ loading: false, questionList: [] })
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const [extraControls, setExtraControls] = useState({ willDeliver: null as null | 'yes' | 'no', quantity: 0 })
  // Modal con la(s) foto(s) de ejemplo: id de la pregunta abierta, o null si está cerrado
  const [openExampleImage, setOpenExampleImage] = useState<number | null>(null)
  const isLiverpoolMexico = configSeller.config.s3.FOLDER_APP === 'ti_liverpool_mx'
  const isMercadoLibreChile = configSeller.config.s3.FOLDER_APP === 'ti_mercado_libre_cl'
  // Mercado Libre cotiza consolas sin controles extra: no se pregunta y la cotización va sin ellos.
  const canQuoteExtraControls = ctx.data.sbu.id_sbu === 12 && !isMercadoLibreChile
  // Liverpool habla de "roturas", no de "trizaduras" (el texto viene de la BD compartida).
  const textoPregunta = (texto: any): any => (isLiverpoolMexico ? sinTrizaduras(texto) : texto)

  const loadData = async () => {
    setData({ ...data, loading: true })
    webApiService.getAllQuestions().then(res => {
      if (res.success) {
        setData({ questionList: res.data, loading: false })
        saveCtx({ ...ctx, steps: { ...ctx.steps, isValid: (ctx.data.answers.length === res.data.filter((q : any) => q.id_parent_question === null).length) } })
      }
    }).catch((error) => {
      console.log(error)
      enqueueSnackbar(error.message, { variant: 'error' })
      setData({ ...data, loading: false })
    })
  }

  const handleChangeQuestion = (event: ChangeEvent<HTMLInputElement>, idPregunta: number) => {
    // console.log('🚀 ~ handleChangeQuestion ~ arrResp:', data.questionList, (event.target as HTMLInputElement).value)

    const arr = [...ctx.data.answers]
    // console.log('🚀 ~ handleChangeQuestion ~ arr:', arr)

    if (arr.some(e => e.id_question === idPregunta)) {
      const idx = arr.findIndex(e => e.id_question === idPregunta)
      // console.log('🚀 ~ handleChangeQuestion ~ idx:', idx)
      arr.splice(idx, 1)
      arr.push({ id_question: idPregunta, id_answer: Number((event.target as HTMLInputElement).value), weigth: data.questionList.find(q => q.id === Number((event.target as HTMLInputElement).value)).weigth, answer: data.questionList.find(q => q.id === Number((event.target as HTMLInputElement).value)).questions })
    } else {
      arr.push({ id_question: idPregunta, id_answer: Number((event.target as HTMLInputElement).value), weigth: data.questionList.find(q => q.id === Number((event.target as HTMLInputElement).value)).weigth, answer: data.questionList.find(q => q.id === Number((event.target as HTMLInputElement).value)).questions })
    }
    // console.log('Entro', ctx.data.answers.length, data.questionList.filter(q => q.id_parent_question === null).length)
    saveCtx({ ...ctx, data: { ...ctx.data, answers: arr }, steps: { ...ctx.steps, isValid: arr.length === data.questionList.filter(q => q.id_parent_question === null).length } })
  }

  useEffect(() => {
    loadData()
    return () => setData({ loading: false, questionList: [] })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Guardar info de controles extra en contexto
  useEffect(() => {
    saveCtx({
      ...ctx,
      data: {
        ...ctx.data,
        // Si no se pregunta por controles extra se guarda null (no cotizados), no 0, para que el
        // backoffice no exija la evidencia de controles ni muestre el módulo de aumentos.
        extraControls: canQuoteExtraControls ? Number(extraControls.quantity) : null
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraControls, canQuoteExtraControls])
  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.3 }}
      className='py-6'
    >
      {
        data.loading
          ? [1, 2, 3].map((p:any, i: number) => {
              return (
                <div key={i} className='border p-4 rounded-md mb-4'>
                  <h4 className='text-lg mb-3'>{p.questions}</h4>
                  <Skeleton className='h-5 w-3/5 rounded-lg' />
                  <div className='grid grid-cols-4 mt-6'>
                    {
                  [1, 2, 3, 4].map((_, k) => {
                    return (
                      <Skeleton key={k} className='h-5 w-3/5 rounded-lg' />
                    )
                  })
                }

                  </div>
                </div>
              )
            })
          : null
      }
      {
        data.questionList.filter(q => q.id_parent_question === null).map((p:any, i: number) => {
          const questionAnswers = data.questionList.filter(a => Number(a.id_parent_question) === p.id)
          const selectedAnswerId = ctx.data.answers.find(a => a.id_question === p.id)?.id_answer
          // Para abolladuras la foto es siempre la misma (no requiere respuesta). Para
          // pantalla/cuerpo, la foto corresponde al grado de la respuesta elegida.
          const exampleImages = getQuestionExampleImages(p.questions, questionAnswers, selectedAnswerId)
          const showIcon = hasQuestionExampleImage(p.questions)
          return (
            <div key={i} className='border p-4 rounded-md mb-4'>
              <h4 className='text-lg mb-3 flex items-center gap-2'>
                {(isLiverpoolMexico && p.id === 3) ? '¿El equipo viene desbloqueado?' : textoPregunta(p.questions)}
                {showIcon && (
                  <button
                    type='button'
                    disabled={!exampleImages}
                    onClick={() => setOpenExampleImage(p.id)}
                    className='text-gray-400 enabled:text-gray-500 enabled:hover:text-black disabled:cursor-not-allowed'
                    aria-label='Ver foto de ejemplo'
                  >
                    <MdOutlinePhotoCamera size={20} />
                  </button>
                )}
              </h4>
              {showIcon && (
                <Modal isOpen={openExampleImage === p.id} onClose={() => setOpenExampleImage(null)} size='2xl'>
                  <ModalContent>
                    <ModalHeader>Foto de ejemplo</ModalHeader>
                    <ModalBody className='pb-6'>
                      <div className='flex flex-wrap gap-3 justify-center'>
                        {exampleImages?.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt='Foto de ejemplo'
                            className='w-full max-w-96 h-auto object-contain rounded-md'
                          />
                        ))}
                      </div>
                    </ModalBody>
                  </ModalContent>
                </Modal>
              )}
              <RadioGroup
                orientation='vertical'
                onChange={(e) => handleChangeQuestion(e, p.id)}
                value={String(ctx.data.answers.find(a => a.id_question === p.id)?.id_answer)}
              >
                {
                  questionAnswers.map((a:any, k: number) => {
                    return (
                      <Radio key={k} value={String(a.id)} style={{ '--seller-color': `${configSeller.config.color}` } as CSSProperties} classNames={{ control: 'bg-black', wrapper: 'group-data-[selected=true]:border-black' }}>{(isLiverpoolMexico && (a.id === 11 || a.id === 7)) ? 'Roto o quebrado' : textoPregunta(a.questions)}</Radio>
                    )
                  })
                }
              </RadioGroup>
            </div>
          )
        })
      }

      {/* Nueva pregunta: Controles extra */}
      <div
        hidden={!canQuoteExtraControls} className='border p-4 rounded-md mb-4'
      >
        <h4 className='text-lg mb-3'>{(isLiverpoolMexico) ? '¿Entregarás controles extra originales y funcionando?' : '¿Entregarás controles extra?'}</h4>
        <RadioGroup
          orientation='horizontal'
          value={extraControls.willDeliver ?? ''}
          onChange={(e) => setExtraControls({ ...extraControls, willDeliver: e.target.value as 'yes' | 'no' })}
        >
          <Radio value='yes' style={{ '--seller-color': `${configSeller.config.color}` } as CSSProperties} classNames={{ control: 'bg-black', wrapper: 'group-data-[selected=true]:border-black' }}>
            Sí, entregare más de un control
          </Radio>
          <Radio value='no' style={{ '--seller-color': `${configSeller.config.color}` } as CSSProperties} classNames={{ control: 'bg-black', wrapper: 'group-data-[selected=true]:border-black' }}>
            No, solo entregaré un control
          </Radio>
        </RadioGroup>

        {extraControls.willDeliver === 'yes' && (
          <div className='mt-4'>
            <label htmlFor='quantity' className='block mb-1'>¿Cuántos controles extra entregarás (Máximo 3)?</label>
            <input
              type='number'
              id='quantity'
              className='border rounded-md p-2 w-32'
              value={extraControls.quantity}
              onChange={(e) => setExtraControls({ ...extraControls, quantity: Number(e.target.value) })}
              min={1}
              max={3}
            />
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default StepTwo
